import { ref, unref } from 'vue'

import { autreEstChoisie, reponseDepuisSaisie, texteAutre } from '../../../shared/question-options.js'
import { questionsVisibles, reponsesDevenuesSansObjet } from '../../utils/questionsConditionnelles.js'
import { partieDe } from '../../../shared/parties-questionnaire.js'

/**
 * Questions d'un questionnaire, dans l'ordre voulu par l'administrateur.
 * Fonction pure, exportée pour être réutilisée telle quelle par tout écran qui
 * affiche des questions de qualification (tunnel d'analyse du besoin,
 * questionnaire d'organisation) sans redéfinir le même tri à chaque endroit.
 */
/**
 * Les questionnaires réduits à une PARTIE : « qualification » (posée par le
 * commercial, relue par le client) ou « organisation » (posée au client seul).
 * Une question sans partie est de qualification : c'est le cas de tout ce qui a
 * été enregistré avant que la partie existe.
 */
export function questionnairesDeLaPartie(questionnaires, partie) {
  return (questionnaires || []).map((questionnaire) => ({
    ...questionnaire,
    questions: (questionnaire.questions || []).filter(
      (question) => partieDe(question) === partie
    ),
  }))
}

export function sortedQuestions(questionnaire) {
  if (!questionnaire?.questions) return []
  return [...questionnaire.questions].sort((a, b) => (a.order || 0) - (b.order || 0))
}

/**
 * Les questions RÉELLEMENT POSÉES d'un questionnaire : celles dont la condition
 * est remplie, dans l'ordre d'affichage.
 *
 * C'est elle que les écrans parcourent, et non `sortedQuestions` : une question
 * conditionnelle dont la branche est fermée ne doit ni s'afficher, ni compter
 * dans les champs obligatoires, ni partir au serveur. Les trois écrans de
 * saisie l'appellent, pour qu'aucun ne puisse en montrer une que les autres
 * taisent.
 */
export function questionsPosees(questionnaire, answersMap) {
  return questionsVisibles(sortedQuestions(questionnaire), answersMap || {})
}

/**
 * Réponse à une question dans une table de réponses indexée par identifiant,
 * ou sa forme par défaut si elle n'a pas encore de valeur. Fonction pure,
 * exportée pour la même raison que `sortedQuestions` ci-dessus : elle sert
 * aussi bien à l'état interne du composable (voir `getAnswer` retourné plus
 * bas) qu'à des réponses reçues en prop par un composant.
 */
export function getAnswer(answersMap, questionId) {
  return answersMap?.[questionId] || { answer: null, selectedOptions: [] }
}

/**
 * Réponses à la « Validation des modalités pédagogiques et organisationnelles »
 * — le questionnaire de qualification.
 *
 * Le même questionnaire se remplit à deux endroits du parcours : dans le tunnel
 * d'analyse du besoin par le commercial, puis dans le questionnaire
 * d'organisation par le client, qui retrouve les réponses déjà données et peut
 * les corriger. Une seule donnée derrière les deux écrans (les réponses de la
 * validation de la demande), donc une seule logique de saisie ici.
 *
 * @param {import('vue').Ref<object[]>|object[]} questionnaires - questionnaires affichés
 * @returns {object} état et helpers de saisie
 */
export function useQualificationAnswers(questionnaires) {
  const answers = ref({})

  /**
   * Identifiants des questions actuellement posées — condition comprise. Une
   * question dont la branche est fermée n'en fait pas partie : sa réponse ne
   * part pas, et le tunnel (qui supprime ce qu'il n'envoie pas) la retire.
   */
  function currentQuestionIds() {
    const ids = new Set()
    for (const questionnaire of unref(questionnaires) || []) {
      for (const question of questionsPosees(questionnaire, answers.value)) {
        ids.add(question.id)
      }
    }
    return ids
  }

  /**
   * Restaure des réponses existantes, qu'elles arrivent en tableau (réponse de
   * la validation) ou en objet indexé par question (questionnaire d'organisation).
   */
  function setAnswers(source) {
    const restored = {}
    const entries = Array.isArray(source)
      ? source.map((entry) => [entry.questionId, entry])
      : Object.entries(source || {})

    for (const [questionId, value] of entries) {
      const id = parseInt(questionId)
      if (Number.isNaN(id)) continue
      restored[id] = {
        answer: value?.answer ?? null,
        selectedOptions: value?.selectedOptions || [],
        // Absent des réponses du questionnaire d'organisation, qui ne le
        // connaît pas : il reste alors `undefined` et ne sera pas réécrit.
        commentaire: value?.commentaire ?? undefined,
        // D'où vient la réponse affichée : sert à signaler une correction faite
        // depuis l'autre écran du parcours.
        updatedBySource: value?.updatedBySource || null,
        updatedByLabel: value?.updatedByLabel || null,
      }
    }
    answers.value = restored
  }

  /**
   * Les types à choix stockent leur valeur dans `selectedOptions`, les autres
   * dans `answer` : mélanger les deux rendrait la réponse illisible côté PDF
   * comme côté récapitulatif. LA SEULE EXCEPTION EST LA CASE « AUTRE », qui
   * remplit les deux — le choix d'un côté, le texte libre de l'autre — et c'est
   * `reponseDepuisSaisie` qui en décide, pour les deux écrans à la fois.
   */
  function handleUpdateAnswer(questionId, value, questionType) {
    const previous = answers.value[questionId] || {}
    answers.value = nettoyerBranchesFermees({
      ...answers.value,
      [questionId]: { ...previous, ...reponseDepuisSaisie(value, questionType) },
    })
  }

  /**
   * La note interne du commercial sur une réponse. Elle vit à côté de la
   * réponse et non dedans : le client remplit le même questionnaire de son côté
   * et ne la voit jamais.
   */
  function handleUpdateComment(questionId, commentaire) {
    const previous = answers.value[questionId] || { answer: null, selectedOptions: [] }
    answers.value = { ...answers.value, [questionId]: { ...previous, commentaire } }
  }

  /**
   * EFFACE CE QUI N'A PLUS D'OBJET. Le client répond à une sous-question, puis
   * change la réponse qui l'avait fait apparaître : sa saisie ne se rattache
   * plus à rien. La garder ferait porter au dossier — et au PDF remis au
   * formateur — une réponse à une question que personne n'a vue.
   *
   * C'est fait ICI, à la source, et non à chaque lecture : le récapitulatif, le
   * PDF, l'export et la reprise commerciale auraient tous eu à s'en souvenir,
   * et il aurait suffi d'un oubli pour que le dossier affirme quelque chose.
   */
  function nettoyerBranchesFermees(table) {
    let propre = table
    for (const questionnaire of unref(questionnaires) || []) {
      for (const id of reponsesDevenuesSansObjet(sortedQuestions(questionnaire), propre)) {
        const { [id]: _efface, ...reste } = propre
        propre = reste
      }
    }
    return propre
  }

  /** Réponses à persister, limitées aux questions réellement posées. */
  function buildAnswersPayload() {
    const ids = currentQuestionIds()
    return Object.entries(answers.value)
      .filter(([questionId]) => ids.has(parseInt(questionId)))
      .map(([questionId, data]) => ({
        questionId: parseInt(questionId),
        answer: data.answer || null,
        selectedOptions: data.selectedOptions || [],
        // `undefined` quand l'écran ne gère pas la note interne : le dépôt sait
        // alors ne pas y toucher, et le client n'efface pas ce que le
        // commercial avait écrit.
        commentaire: data.commentaire,
      }))
  }

  /** Questions obligatoires restées sans réponse, avec leur identifiant. */
  function missingRequiredQuestions() {
    const missing = []
    for (const questionnaire of unref(questionnaires) || []) {
      // UNE QUESTION MASQUÉE NE PEUT PAS ÊTRE OBLIGATOIRE : elle bloquerait
      // l'envoi sur un champ que personne ne voit à l'écran, et rien ne dirait
      // lequel.
      for (const question of questionsPosees(questionnaire, answers.value)) {
        if (!question.required || question.type === 'info') continue
        const answer = answers.value[question.id]
        const hasAnswer = Boolean(
          (answer?.answer !== null && answer?.answer !== undefined && String(answer.answer).trim() !== '') ||
          (Array.isArray(answer?.selectedOptions) && answer.selectedOptions.length > 0)
        )
        // « Autre » cochée sans rien derrière, c'est une question restée sans
        // réponse : le choix seul ne dit rien, c'est le texte qui répond.
        const autreVide = autreEstChoisie(question, answer) && !texteAutre(answer)
        if (!hasAnswer || autreVide) missing.push({ id: question.id, label: question.label })
      }
    }
    return missing
  }

  /** Libellés des questions obligatoires restées sans réponse. */
  function missingRequiredLabels() {
    return missingRequiredQuestions().map((question) => question.label)
  }

  return {
    answers,
    // Lié à l'état interne : le module exporte la version pure (answersMap, questionId).
    getAnswer: (questionId) => getAnswer(answers.value, questionId),
    setAnswers,
    handleUpdateAnswer,
    handleUpdateComment,
    sortedQuestions,
    // Liée elle aussi à l'état interne, pour que l'appelant n'ait pas à lui
    // repasser les réponses à chaque rendu.
    questionsPosees: (questionnaire) => questionsPosees(questionnaire, answers.value),
    buildAnswersPayload,
    missingRequiredLabels,
    missingRequiredQuestions,
  }
}
